import { mapState } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
const kTimeAggregationDefault = {
  timeZone: 'Europe/Berlin',
  firstWeekDay: 'Monday',
  bindedKey: ''
};
export default {
  name: 'CoreDtwinStateTimeseriesAggregateEdit',
  props: {
    // the current edited binding
    lclModel: {
      type: Object,
      required: true
    },
    otherStates: {
      type: Array,
      required: true
    },
    tenantId: {
      type: Number,
      required: true
    },
    // is this an 'edit', 'create', or mass-variable- 'import'?
    mode: {
      type: String,
      required: false,
      default: () => 'edit'
    },
    templateContext: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function () {
    return {
      timeAggregation: cloneDeep(kTimeAggregationDefault),
      offset: null,
      offsetUnit: null,
      periodLength: null,
      periodUnit: null
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme']),
    availableDurations() {
      return [{
        value: 15 * 60,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.timeRanges.15m')
      }, {
        value: 30 * 60,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.timeRanges.30m')
      }, {
        value: 60 * 60,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.timeRanges.1h')
      }, {
        value: 3 * 60 * 60,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.timeRanges.3h')
      }, {
        value: 6 * 60 * 60,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.timeRanges.6h')
      }, {
        value: 12 * 60 * 60,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.timeRanges.12h')
      }, {
        value: 24 * 60 * 60,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.timeRanges.24h')
      }, {
        value: 2 * 24 * 60 * 60,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.timeRanges.2d'),
        forFixed: false
      }, {
        value: 7 * 24 * 60 * 60,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.timeRanges.7d')
      }, {
        value: 14 * 24 * 60 * 60,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.timeRanges.2w'),
        forFixed: false
      }, {
        value: 30 * 24 * 60 * 60,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.timeRanges.1M')
      }, {
        value: 90 * 24 * 60 * 60,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.timeRanges.3M')
      }, {
        value: 180 * 24 * 60 * 60,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.timeRanges.6M')
      }, {
        value: 365 * 24 * 60 * 60,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.timeRanges.1Y')
      }, {
        value: 'now/h',
        text: this.$i18n_t('Core.DateTimePicker.rangeItems.quick.currentHour')
      }, {
        value: 'now-1h/h',
        text: this.$i18n_t('Core.DateTimePicker.rangeItems.quick.periousHour'),
        forFixed: true
      }, {
        value: 'now/d',
        text: this.$i18n_t('Core.DateTimePicker.rangeItems.quick.today')
      }, {
        value: 'now-1d/d',
        text: this.$i18n_t('Core.DateTimePicker.rangeItems.quick.yesterday'),
        forFixed: true
      }, {
        value: 'now/w',
        text: this.$i18n_t('Core.DateTimePicker.rangeItems.quick.thisWeek')
      }, {
        value: 'now/M',
        text: this.$i18n_t('Core.DateTimePicker.rangeItems.quick.thisMonth')
      }, {
        value: 'now/y',
        text: this.$i18n_t('Core.DateTimePicker.rangeItems.quick.thisYear')
      }, {
        value: 'now-1w/w',
        text: this.$i18n_t('Core.DateTimePicker.rangeItems.quick.previousWeek'),
        forFixed: true
      }, {
        value: 'now-1M/M',
        text: this.$i18n_t('Core.DateTimePicker.rangeItems.quick.previousMonth'),
        forFixed: true
      }, {
        value: 'now-1y/y',
        text: this.$i18n_t('Core.DateTimePicker.rangeItems.quick.previousYear'),
        forFixed: true
      }, {
        value: 'now-2d/d',
        text: this.$i18n_t('Core.DateTimePicker.rangeItems.quick.dayBeforeYesterday'),
        forFixed: true
      }, {
        value: 'now-7d/d',
        text: this.$i18n_t('Core.DateTimePicker.rangeItems.quick.thisDayLastWeek'),
        forFixed: true
      }].filter(d => {
        return !this.timeAggregation.fixedTimestamp || typeof d.value !== 'number' && d.forFixed === true;
      });
    },
    availableOperators() {
      return [{
        value: 'avg',
        text: 'AVG'
      }, {
        value: 'min',
        text: 'MIN'
      }, {
        value: 'max',
        text: 'MAX'
      }, {
        value: 'sum',
        text: 'SUM'
      }, {
        value: 'median',
        text: 'MEDIAN'
      }, {
        value: 'count',
        text: 'COUNT'
      }, {
        value: 'diff',
        text: this.$i18n_t('DigitalTwin.DigitalTwin.states.timeseries-aggregate.difference'),
        desc: this.$i18n_t('DigitalTwin.DigitalTwin.states.timeseries-aggregate.differenceText')
      }, {
        value: 'latest_diff',
        text: this.$i18n_t('DigitalTwin.DigitalTwin.states.timeseries-aggregate.lastDifference'),
        desc: this.$i18n_t('DigitalTwin.DigitalTwin.states.timeseries-aggregate.lastDifferenceText')
      }];
    },
    timeUnits() {
      return [{
        value: 'm',
        text: this.$i18n_t('Core.DateTimePicker.rangeItems.units.minutes')
      }, {
        value: 'h',
        text: this.$i18n_t('Core.DateTimePicker.rangeItems.units.hours')
      }, {
        value: 'd',
        text: this.$i18n_t('Core.DateTimePicker.rangeItems.units.days')
      }, {
        value: 'w',
        text: this.$i18n_t('Core.DateTimePicker.rangeItems.units.weeks')
      }, {
        value: 'M',
        text: this.$i18n_t('Core.DateTimePicker.rangeItems.units.months')
      }, {
        value: 'y',
        text: this.$i18n_t('Core.DateTimePicker.rangeItems.units.years')
      }];
    },
    requiredRule() {
      return [v => !!v || parseInt(v) === 0 || this.$i18n_t('Common.fieldRequired')];
    },
    hourRules() {
      return [...this.requiredRule, v => v >= 0 && v <= 23 || this.$i18n_t('DigitalTwin.DigitalTwin.ruleModeler.timeTriggers.hourRule')];
    },
    minuteRules() {
      return [...this.requiredRule, v => v >= 0 && v <= 59 || this.$i18n_t('DigitalTwin.DigitalTwin.ruleModeler.timeTriggers.minuteRule')];
    }
  },
  watch: {
    lclModel() {
      this.timeAggregation = this.lclModel.properties.binding.options.timeAggregation || cloneDeep(kTimeAggregationDefault);
      this.calcMomentStringComponents();
    },
    'timeAggregation.stateId'() {
      this.lclModel.properties.binding.options.timeAggregation.stateId = this.timeAggregation.stateId;
      const state = this.otherStates.find(s => s.id === this.timeAggregation.stateId);
      if (!this.lclModel.properties.faIcon) {
        this.lclModel.properties.faIcon = state.properties.faIcon;
      }
      if (!this.lclModel.properties.unit) {
        this.lclModel.properties.unit = state.properties.unit;
      }
      this.lclModel.properties = cloneDeep(this.lclModel.properties);
    },
    'timeAggregation.duration'() {
      this.lclModel.properties.binding.options.timeAggregation.duration = this.timeAggregation.duration;
    },
    'timeAggregation.fixedTimestamp'() {
      this.lclModel.properties.binding.options.timeAggregation.fixedTimestamp = this.timeAggregation.fixedTimestamp;
      if (this.timeAggregation.fixedTimestamp) {
        // default time-range to one of the fixed durations
        if (this.availableDurations.findIndex(d => d.value === this.timeAggregation.duration) < 0) {
          this.timeAggregation.duration = this.availableDurations[0].value;
        }
      }
      this.lclModel.properties.binding.options.timeAggregation.duration = this.timeAggregation.duration;
    },
    'timeAggregation.operator'() {
      this.lclModel.properties.binding.options.timeAggregation.operator = this.timeAggregation.operator;
    },
    'timeAggregation.timeZone'() {
      this.lclModel.properties.binding.options.timeAggregation.timeZone = this.timeAggregation.timeZone;
    },
    'timeAggregation.firstWeekDay'() {
      this.lclModel.properties.binding.options.timeAggregation.firstWeekDay = this.timeAggregation.firstWeekDay;
    }
  },
  mounted() {
    this.lclModel.properties.binding.options = {
      ...this.lclModel.properties.binding.options,
      timeAggregation: {
        ...(this.lclModel.properties.binding.options.timeAggregation || cloneDeep(kTimeAggregationDefault))
      }
    };
    this.timeAggregation = this.lclModel.properties.binding.options.timeAggregation;
    this.timeAggregation.key = this.lclModel.key;
    this.calcMomentStringComponents();
  },
  methods: {
    calcMomentStringComponents() {
      if (isNaN(parseInt(this.timeAggregation.duration))) {
        const matches = ((this.timeAggregation.duration || '').match(/^(now)(?:([-+]{1})([0-9]+)([mhdwMy]))?(?:\/([0-9]*)([hdwMy]+))?$/) || []).slice(2);
        this.offset = (get(matches, '[0]') === '-' ? 1 : -1) * parseInt(get(matches, '[1]'), 10) || 0;
        this.offsetUnit = get(matches, '[2]') || get(matches, '[4]') || 'm';
        this.periodLength = get(matches, '[3]') || 1;
        this.periodUnit = get(matches, '[4]') || 'm';
      } else {
        const unitMapping = [{
          length: 60,
          unit: 'm'
        }, {
          length: 3600,
          unit: 'h'
        }, {
          length: 86400,
          unit: 'd'
        }, {
          length: 86400 * 7,
          unit: 'w'
        }, {
          length: 86400 * 30,
          unit: 'M'
        }, {
          length: 86400 * 365,
          unit: 'y'
        }];
        let i = 1;
        while (i < unitMapping.length) {
          const currentMapping = unitMapping[i];
          if (Math.floor(this.timeAggregation.duration / currentMapping.length) < 1) {
            i--;
            break;
          }
          i++;
        }
        // Start Insert MWa stabilization Release 21.12.2021
        if (i === unitMapping.length) {
          i = unitMapping.length - 1;
        }
        // End Insert MWa stabilization Release 21.12.2021
        this.offset = 0;
        this.offsetUnit = unitMapping[i].unit;
        this.periodLength = Math.floor(this.timeAggregation.duration / unitMapping[i].length);
        this.periodUnit = unitMapping[i].unit;
      }
    },
    calcMomentString() {
      this.timeAggregation.duration = `now-${this.offset}${this.offsetUnit}/${this.periodLength}${this.periodUnit}`;
      this.timeAggregation = cloneDeep(this.timeAggregation);
    }
  }
};